import { Chip, Input, Spinner } from "@heroui/react";
import { Button } from "components/button/button.tsx";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "components/modal";
import { useEffect, useRef, useState } from "react";
import {
  Bookmark,
  Calendar,
  Checkmark,
  Close,
  Link,
  User,
  ArrowRight,
  Building,
  Document,
  Folder,
} from "assets/icons";
import { Textarea } from "components/textarea/textarea.tsx";
import { API_URL } from "constants/index";
import { UserSelection } from "../userSelection/userSelection";
import { toast } from "react-toastify";
import ResourceSelection from "../resourceSelection/resourceSelection";
import LabelSelection from "../labelSelection/labelSelection";
import { useSession, useUser } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useTasks } from "shared/contexts/tasks.context";
import { Task, TaskLabel, TaskLabels, TaskPriority } from "shared/types/task";
import { Resource, ResourceType } from "shared/types/resource";
import { ClerkUser } from "shared/types/user";
import { getIconForLabel } from "shared/utils/get-icons";
import { formatISODateForInput } from "shared/utils/get-dates";
import Chatter from "shared/components/chatter/chatter";

export type AvailableLabel = {
  name: TaskLabel;
  icon: JSX.Element | null;
};

export type AvailableLabels = AvailableLabel[];

interface ViewTaskModalProps {
  task: Task | null;
  onClose: () => void;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  fetchTasks?: () => void;
}

const ViewTaskModal: React.FC<ViewTaskModalProps> = ({
  task,
  onClose,
  isOpen = false,
  onOpenChange,
}) => {
  const { refreshTasks } = useTasks();
  const [title, setTitle] = useState("");
  const [selectedPriority, setSelectedPriority] = useState(0);
  const [description, setDescription] = useState("");
  const [taskLabels, setTaskLabels] = useState<TaskLabels[]>([]);
  const [taskDate, setTaskDate] = useState("");
  const [selectedResources, setSelectedResources] = useState<Resource[]>([]);
  const [selectedAssignee, setSelectedAssignee] = useState<ClerkUser | null>(
    null,
  );
  const [selectedAssignedBy, setSelectedAssignedBy] =
    useState<ClerkUser | null>(null);
  const { session } = useSession();
  const { user } = useUser();
  const [files, setFiles] = useState<
    { file: File; sharePointFolderId?: string }[]
  >([]);
  // const [setTotalFileSize] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveToCalendar] = useState(false);
  const dateInputRef = useRef<HTMLInputElement | null>(null);

  const resetState = () => {
    setTitle("");
    setSelectedPriority(0);
    setDescription("");
    setTaskLabels([]);
    setTaskDate("");
    setSelectedResources([]);
    setSelectedAssignee(null);
    setSelectedAssignedBy(null);
    setFiles([]);
    // setTotalFileSize(0);
  };

  useEffect(() => {
    if (isOpen && task) {
      // Populate states with task data
      setTitle(task.title);
      setDescription(task.description);
      setTaskLabels(task.labels);
      const formattedDate = task.deadline
        ? formatISODateForInput(task.deadline)
        : "";
      setTaskDate(formattedDate);
      setSelectedPriority(task.priority);
      setSelectedResources(task.resources);
      setSelectedAssignee(task.assignee);
      setSelectedAssignedBy(task.assignedBy);
    }
  }, [isOpen, task]);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  const availableLabels: AvailableLabels = Object.values(TaskLabel)
    .map((label) => ({
      icon: getIconForLabel(label),
      name: label,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  // const selectedLabels: AvailableLabels = Object.values(TaskLabel).map(label => ({
  //     icon: getIconForLabel(label),
  //     name: label
  // }));

  // const handleSwitchToggle = () => {
  //     setSaveToCalendar(!saveToCalendar);
  // };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen, onOpenChange]);

  const handleInputChange = (event: any) => {
    setTitle(event.target.value);
  };

  const today = new Date().toISOString().split("T")[0];

  // const addResourceConnection = (resource: Resource) => {
  //     setSelectedResources(prevResources => [...prevResources, resource]);
  // };

  // const removeResourceConnection = (resourceId: Id) => {
  //     setSelectedResources(prevResources => prevResources.filter(resource => resource.id !== resourceId));
  // };

  // const handleRemoveFile = (indexToRemove: number, size: number) => {
  //     setFiles(prevFiles => prevFiles.filter((_, index) => index !== indexToRemove));
  //     setTotalFileSize(prevTotalFileSize => prevTotalFileSize - size / (1024 * 1024)); // Reduce the size from total
  // };

  const saveTask = async () => {
    if (!title.trim()) {
      toast.error("De taak moet een titel hebben!");
      return; // Do not proceed.
    }

    if (!selectedAssignee) {
      toast.error("Wie gaat de taak uitvoeren?");
      return; // Do not proceed.
    }
    setIsSaving(true);
    const formData = new FormData();

    if (!task?.id) return;

    formData.append("id", task?.id as any);
    formData.append("title", title);
    formData.append("description", description);
    formData.append("priority", selectedPriority.toString());
    formData.append(
      "deadline",
      taskDate ? new Date(taskDate).toISOString() : "",
    );
    formData.append(
      "resources",
      JSON.stringify(selectedResources.map((resource) => resource.id)),
    );
    formData.append(
      "labels",
      JSON.stringify(taskLabels.map((label) => ({ name: label.name }))),
    );

    if (selectedAssignedBy) {
      if (typeof selectedAssignedBy === "string") {
        formData.append(
          "assignedBy",
          JSON.stringify({ id: selectedAssignedBy }),
        );
      } else {
        formData.append(
          "assignedBy",
          JSON.stringify({
            id: selectedAssignedBy.id,
            email: selectedAssignedBy.email_addresses[0].email_address,
            firstName: selectedAssignedBy.first_name,
          }),
        );
      }
    } else {
      formData.append(
        "assignedBy",
        JSON.stringify({
          id: user?.id,
          email: user?.emailAddresses[0].emailAddress,
          firstName: user?.firstName,
        }) || JSON.stringify({ id: "" }),
      );
    }

    if (saveToCalendar && selectedAssignee) {
      const sharepointUser = {
        sharepointUserId: selectedAssignee.public_metadata.sharepointUserId,
        sharepointCalendarId:
          selectedAssignee.public_metadata.sharepointCalendarId,
      };
      formData.append("sharepointUser", JSON.stringify(sharepointUser));
    }

    if (selectedAssignee) {
      if (typeof selectedAssignee === "string") {
        formData.append("assignee", JSON.stringify({ id: selectedAssignee }));
      } else {
        formData.append(
          "assignee",
          JSON.stringify({
            id: selectedAssignee.id,
            email: selectedAssignee.email_addresses[0].email_address,
            firstName: selectedAssignee.first_name,
          }),
        );
      }
    }

    // Adding files
    files.forEach((fileObj, index) => {
      formData.append("file" + index, fileObj.file);
      if (fileObj.sharePointFolderId) {
        formData.append("folderId" + index, fileObj.sharePointFolderId);
      }
    });

    try {
      const response = await fetchWithAuth(
        `${API_URL}/tasks/update`,
        {
          method: "PUT",
          body: formData,
        },
        session,
      );

      await response.json();
      toast.success("Task saved!");
      await refreshTasks();
      onClose();
    } catch (error) {
      console.error("There was an error saving the task:", error);
    } finally {
      setIsSaving(false);
      onClose();
    }
  };

  if (!isOpen || !task) return null;

  return (
    <>
      {isSaving && (
        <div className="absolute left-0 top-0 z-50 flex h-full w-full items-center justify-center bg-black bg-opacity-50">
          <Spinner size="lg" className="mr-5" />{" "}
          <span className="bold text-xl text-brick-primary">Saving...</span>
        </div>
      )}
      {!isSaving && (
        <Modal
          size="4xl"
          hideCloseButton
          className="z-0"
          isOpen={isOpen}
          onOpenChange={() => {
            onOpenChange(!isOpen);
            onClose();
          }}
        >
          <ModalContent className="grow overflow-y-auto p-3.5 lg:min-w-212.5">
            {(onClose) => (
              <div className="flex h-[80vh] flex-col">
                <ModalFooter className="sticky bottom-0 mb-7.5 bg-white p-0">
                  <Button
                    variant="bordered"
                    radius="full"
                    startContent={<Checkmark />}
                    className="border-1 border-moss-primary text-moss-primary"
                    onClick={saveTask}
                  >
                    Bewaren
                  </Button>
                  <Button
                    variant="bordered"
                    radius="full"
                    startContent={<Close />}
                    className="border-1 border-neutral-secondary-mid text-neutral-secondary-mid"
                    onPress={onClose}
                  >
                    Sluiten
                  </Button>
                </ModalFooter>

                <ModalHeader className="w-full p-0">
                  <Input
                    label="Titel"
                    onChange={handleInputChange}
                    isRequired
                    value={title}
                  />
                </ModalHeader>

                <ModalBody className="mt-2 gap-6 p-0">
                  <Textarea
                    label="Beschrijving"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                  <div className="flex justify-between rounded-md bg-secondary p-2.5">
                    <div className="flex flex-wrap items-center gap-4">
                      <div
                        className="flex items-center gap-2 text-sm"
                        onClick={() => dateInputRef.current?.focus()}
                      >
                        <Calendar />

                        <input
                          ref={dateInputRef}
                          type="date"
                          value={taskDate}
                          min={today}
                          onChange={(e) => {
                            setTaskDate(e.target.value);
                          }}
                          className="rounded-md border-none p-1 focus:ring-2 focus:ring-primary"
                        />
                      </div>
                      <div className="flex  items-center gap-2">
                        <LabelSelection
                          taskLabels={taskLabels}
                          setTaskLabels={setTaskLabels}
                          availableLabels={availableLabels}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <UserSelection
                          defaultUser={
                            selectedAssignedBy ? selectedAssignedBy : user
                          }
                          onUserSelect={setSelectedAssignedBy}
                        />
                        <ArrowRight />
                        <UserSelection
                          defaultUser={
                            selectedAssignee
                              ? (selectedAssignee as ClerkUser)
                              : null
                          }
                          onUserSelect={setSelectedAssignee}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <ResourceSelection defaultResources={task.resources} />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          color="success"
                          variant={
                            selectedPriority == TaskPriority.LOW
                              ? "solid"
                              : "flat"
                          }
                          onClick={() => setSelectedPriority(TaskPriority.LOW)}
                        >
                          Laag
                        </Button>
                        <Button
                          size="sm"
                          color="warning"
                          variant={
                            selectedPriority == TaskPriority.MEDIUM
                              ? "solid"
                              : "flat"
                          }
                          onClick={() =>
                            setSelectedPriority(TaskPriority.MEDIUM)
                          }
                        >
                          Gemiddeld
                        </Button>
                        <Button
                          size="sm"
                          color="danger"
                          variant={
                            selectedPriority == TaskPriority.HIGH
                              ? "solid"
                              : "flat"
                          }
                          onClick={() => setSelectedPriority(TaskPriority.HIGH)}
                        >
                          Hoog
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* {selectedAssignee && selectedAssignee.public_metadata.sharepointUserId && selectedAssignee.public_metadata.sharepointCalendarId && taskDate && (
                                    <div onClick={handleSwitchToggle}>
                                    <Switch onClick={() => setSaveToCalendar(!saveToCalendar)} defaultSelected={false} isSelected={saveToCalendar}>
                                        Voeg afspraak toe in outlook kalender van {selectedAssignee.first_name + " " + selectedAssignee.last_name}
                                    </Switch>
                                    </div>
                                )} */}

                  <hr />

                  <div className="flex items-center gap-2">
                    <Bookmark />
                    <p>Bijlagen</p>
                  </div>

                  {task.attachments &&
                    task.attachments.map((attachment, idx) => (
                      <div key={idx} className="p-2">
                        {/* Display the name of the attachment */}
                        <div className="font-bold mb-2">
                          {attachment.fileName}
                        </div>

                        <div className="flex items-center gap-3">
                          {/* Document Button */}
                          <a
                            href={attachment.sharePointFileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 rounded-md px-3 py-1 transition hover:bg-gray-100"
                          >
                            <Document />
                            <span>Bekijk document</span>
                            <ArrowRight />
                          </a>

                          {/* Folder Button */}
                          <a
                            href={attachment.sharePointFolderUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 rounded-md px-3 py-1 transition hover:bg-gray-100"
                          >
                            <Folder />
                            <span>Open folder</span>
                            <ArrowRight />
                          </a>
                        </div>
                      </div>
                    ))}
                  {/* <div
                                        className='border-1 border-dashed p-4 flex flex-col gap-2'
                                        onClick={() => document.getElementById('fileInput')?.click()} // Triggering click event on hidden input
                                    >
                                        {files.length > 0 ? (
                                            files.map((fileObj, index) => (
                                                <div key={fileObj.file.name} className="flex items-center gap-2">
                                                    <span><Document /></span>
                                                    <span>{fileObj.file.name} ({(fileObj.file.size / (1024 * 1024)).toFixed(2)} MB)</span>
                                                    <button onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRemoveFile(index, fileObj.file.size)
                                                    }}>x</button>

                                                    <select
                                                        value={fileObj.sharePointFolderId || ''}
                                                        onChange={e => {
                                                            const newFolderId = e.target.value;
                                                            setFiles(prevFiles => prevFiles.map((f, idx) => idx === index
                                                                ? { ...f, sharePointFolderId: newFolderId }
                                                                : f
                                                            ));
                                                        }}
                                                        onClick={e => e.stopPropagation()}
                                                    >
                                                        <option value="">Standaard taak folder</option>
                                                        {selectedResources.map(resource => (
                                                            <option key={resource.id} value={resource.sharePointFolderId}>
                                                                {resource.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            ))
                                        ) : (
                                            <p>Sleep een bestand of klik op de knop om een bijlage toe te voegen</p>
                                        )}
                                        <input
                                            type="file"
                                            id="fileInput"
                                            multiple
                                            style={{ display: "none" }}
                                            onChange={e => {
                                                if (e.target.files) {
                                                    const newFiles = Array.from(e.target.files).map(file => ({
                                                        file,
                                                        sharePointFolderId: '',
                                                        sharePointFolderUrl: ''
                                                    }));
                                                    setFiles(prevFiles => [...prevFiles, ...newFiles]);

                                                    let total = totalFileSize;
                                                    for (let i = 0; i < newFiles.length; i++) {
                                                        total += newFiles[i].file.size;
                                                    }
                                                    setTotalFileSize(total / (1024 * 1024));  // Convert size to MB
                                                }
                                            }}
                                        />
                                        <Button onClick={() => document.getElementById('fileInput')?.click()} radius='full' className='bg-neutral-primary text-white' startContent={<Plus />}>Selecteer bestand</Button>
                                    </div> * /} */}
                  <hr />
                  <div className="flex items-center gap-2">
                    <Link />
                    <p>Koppelingen</p>
                  </div>
                  {selectedResources &&
                    selectedResources.length > 0 &&
                    selectedResources.map((resource) => (
                      <div
                        key={resource.id}
                        className="relative flex items-center justify-between border-1 border-solid p-4"
                      >
                        <div>
                          {resource.type === "building" ? (
                            <Building />
                          ) : resource.type === ResourceType.CONTACT ? (
                            <User />
                          ) : null}
                          <h4 className="font-bold">{resource.name}</h4>
                          <p>{resource.metadata1}</p>
                          <div className="mt-1 flex gap-1">
                            {resource.metadata2
                              ?.split(",")
                              .map((label, idx) => (
                                <Chip key={idx}>{label}</Chip>
                              ))}
                          </div>
                        </div>
                        <a href={`/${resource.type}/${resource.resourceId}`}>
                          <ArrowRight />
                        </a>
                      </div>
                    ))}
                  <Chatter task={task} />
                </ModalBody>
              </div>
            )}
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default ViewTaskModal;
